// White-label theme bundle (CB-12, CB-16). This directory is the ONLY place brand
// identity and design token VALUES may live (INF-1 / gate exemption for theme/).
// theme.css is the runtime source; this file is its typed mirror for TS consumers
// (charts, canvas, tests) plus the status tone → CSS-var map (CB-15).
// Values: admin design `T()` (`BookingDei Super Admin.dc.html` L7246-7285), light +
// dark, transcribed verbatim — VIS §1.4.
import { statusToneOrNeutral, STATUS_TONES } from '@bookingdei/i18n/status-tone';
import type { StatusTone } from '@bookingdei/i18n/status-tone';
import type { StatusEntity } from '@bookingdei/i18n/status-label';

export const brand = {
  name: 'BookingDei',
  vendor: 'BetterDots',
  version: 'v0.1 demo',
  console: 'Admin console',
} as const;

// ---- T() light + dark (typed mirror of theme.css) ----
export interface ThemeTokens {
  canvas: string;
  shell: string;
  panel: string;
  panel2: string;
  line: string;
  hairline: string;
  ink: string;
  muted: string;
  muted2: string;
  navy: string;
  onNavy: string;
  accent: string;
  accentSoft: string;
  accentInk: string;
  onAccent: string;
  neutralSoft: string;
  hero: string;
  heroLine: string;
  rowHover: string;
  chartGrid: string;
  ok: string;
  okFg: string;
  okSoft: string;
  warn: string;
  warnFg: string;
  warnSoft: string;
  info: string;
  infoFg: string;
  infoSoft: string;
  danger: string;
  dangerFg: string;
  dangerSoft: string;
  dangerStrong: string;
  s1: string;
  s2: string;
  s3: string;
  sPale: string;
  shadow: string;
  rail: string;
  railLine: string;
  railChip: string;
  railActive: string;
  railInk: string;
  railLabel: string;
  onTile: string;
  hues: ReadonlyArray<{ body: string; tile: string; sub: string; line: string }>;
}

export const light: ThemeTokens = {
  canvas: '#E5EFFC', shell: '#F0F5FD', panel: '#FFFFFF', panel2: '#E5EFFC', line: '#DCE7F6', hairline: '#E3ECF9',
  // DC123: muted / muted2 darkened from #5B6673 / #616C79 for 4.5:1 on accent-soft + rail.
  ink: '#1B2530', muted: '#525C68', muted2: '#555F6B', navy: '#0E1B2C', onNavy: '#E9F1F5',
  accent: '#0E7490', accentSoft: '#BDE3FB', accentInk: '#0A5B70', onAccent: '#FFFFFF',
  neutralSoft: '#E5EFFC', hero: '#0E1B2C', heroLine: 'rgba(255,255,255,0.13)',
  rowHover: '#F0F5FD', chartGrid: '#DFE9F7',
  ok: '#0F7A4E', okFg: '#0C6340', okSoft: '#DDECE6',
  warn: '#B87309', warnFg: '#8A5406', warnSoft: '#F4E9D8',
  info: '#0E7490', infoFg: '#0A5B70', infoSoft: '#BDE3FB',
  danger: '#C2313A', dangerFg: '#C2313A', dangerSoft: '#FDF0EC', dangerStrong: '#C2313A',
  s1: '#0E7490', s2: '#5B98AC', s3: '#0E1B2C', sPale: '#BDE3FB',
  shadow: '0 12px 32px rgba(14,27,44,0.09)',
  rail: 'rgba(195,214,221,0.5)', railLine: 'rgba(12,26,36,0.13)', railChip: 'rgba(255,255,255,0.55)',
  railActive: '#FFFFFF', railInk: '#0C1A24', railLabel: '#2E4653',
  onTile: '#FFFFFF',
  hues: [
    { body: '#C5DBEF', tile: '#43708F', sub: '#40525F', line: '#B4CFE7' },
    { body: '#E9EAFC', tile: '#4E52B6', sub: '#4A4F72', line: '#D6D8F6' },
    { body: '#F8F1E5', tile: '#8A6222', sub: '#5C4A2E', line: '#EADFCA' },
    { body: '#FCE8EC', tile: '#A03A55', sub: '#6A4550', line: '#F5D3DB' },
  ],
};

export const dark: ThemeTokens = {
  canvas: '#0A121C', shell: '#0E1B2C', panel: '#16232F', panel2: '#0F1B26', line: '#25333F', hairline: '#1E2C38',
  ink: '#E8EEF4', muted: '#A8B6C4', muted2: '#95A5B4', navy: '#0E1B2C', onNavy: '#E9F1F5',
  accent: '#7FB8CC', accentSoft: 'rgba(127,184,204,0.14)', accentInk: '#A9CDF7', onAccent: '#07131C',
  neutralSoft: 'rgba(255,255,255,0.08)', hero: '#0E1B2C', heroLine: 'rgba(255,255,255,0.10)',
  rowHover: '#1B2836', chartGrid: 'rgba(255,255,255,0.06)',
  ok: '#3FBB7D', okFg: '#8FE0B4', okSoft: 'rgba(63,187,125,0.16)',
  warn: '#E0A03A', warnFg: '#F2C785', warnSoft: 'rgba(224,160,58,0.18)',
  info: '#7FB8CC', infoFg: '#A9CDF7', infoSoft: 'rgba(127,184,204,0.16)',
  danger: '#FF8A8F', dangerFg: '#FF8A8F', dangerSoft: 'rgba(255,138,143,0.14)', dangerStrong: '#B3242C',
  s1: '#7FB8CC', s2: '#5B98AC', s3: '#A9C6D4', sPale: 'rgba(127,184,204,0.22)',
  shadow: 'none',
  rail: 'rgba(18,33,45,0.5)', railLine: 'rgba(255,255,255,0.10)', railChip: 'rgba(255,255,255,0.07)',
  railActive: 'rgba(255,255,255,0.08)', railInk: '#E9F1F5', railLabel: '#8FA3B2',
  onTile: '#07131C',
  hues: [
    { body: '#1A2A38', tile: '#6E93B0', sub: '#A8B6C4', line: '#284056' },
    { body: '#21223C', tile: '#8A8EE8', sub: '#B3B7DA', line: '#33355C' },
    { body: '#2A2317', tile: '#C79A45', sub: '#D2BE96', line: '#413623' },
    { body: '#31202A', tile: '#D2687F', sub: '#DEAEB9', line: '#4A2E3A' },
  ],
};

// ---- chip pairs (design `chip(bg,color)` + dark `tone()` map) ----
// Eight tone pairs, light → dark; each is a pair of CSS vars in theme.css.
export type ChipTone = 'neutral' | 'warn' | 'ok' | 'danger' | 'info' | 'violet' | 'purple' | 'navy';

export const chipPairs: Record<ChipTone, { light: [bg: string, fg: string]; dark: [bg: string, fg: string] }> = {
  neutral: { light: ['#E5EFFC', '#5B6673'], dark: ['rgba(255,255,255,0.08)', '#A8B6C4'] },
  warn: { light: ['#F4E9D8', '#8A5406'], dark: ['rgba(224,160,58,0.18)', '#F2C785'] },
  ok: { light: ['#DDECE6', '#0C6340'], dark: ['rgba(63,187,125,0.16)', '#8FE0B4'] },
  danger: { light: ['#FDF0EC', '#C2313A'], dark: ['rgba(255,138,143,0.14)', '#FF8A8F'] },
  info: { light: ['#BDE3FB', '#0A5B70'], dark: ['rgba(127,184,204,0.16)', '#A9CDF7'] },
  violet: { light: ['#EDE9FE', '#6D28D9'], dark: ['rgba(168,85,247,0.16)', '#C4B5FD'] },
  purple: { light: ['#F3E8FF', '#7C3AED'], dark: ['rgba(168,85,247,0.18)', '#D2A8FF'] },
  navy: { light: ['#0E1B2C', '#E9F1F5'], dark: ['#0E1B2C', '#E9F1F5'] },
};

/** Runtime style for a chip tone: CSS vars, so the active theme decides. */
export function chipStyle(tone: ChipTone): { background: string; color: string } {
  return { background: `var(--chip-${tone}-bg)`, color: `var(--chip-${tone}-fg)` };
}

/** Same pair in the v1 `{ fg, bg }` shape older screens destructure. */
export function chipColors(tone: ChipTone): { fg: string; bg: string } {
  return { fg: `var(--chip-${tone}-fg)`, bg: `var(--chip-${tone}-bg)` };
}

// ---- status → tone (CB-15: packages/i18n status-tone decides the tone) ----
/** i18n's five tones map 1:1 onto the admin chip pairs. */
export const statusToneChip: Record<StatusTone, ChipTone> = {
  ok: 'ok',
  warn: 'warn',
  info: 'info',
  danger: 'danger',
  neutral: 'neutral',
};

// Entities searched, in order, when a caller has only the raw status string (the
// console's legacy chip call sites predate entity-aware chips). First hit wins.
const ENTITY_ORDER: StatusEntity[] = [
  'booking_status',
  'business_status',
  'application_status',
  'subscription_payment_status',
  'subscription_status',
  'invoice_status',
  'dispute_status',
  'review_status',
  'offer_status',
  'claim_status',
  'claim_state',
  'catalog_item_status',
  'payment_kyc_state',
  'proof_status',
  'dispute_resolution',
];

// Admin-only wire values with no DC08 canonical entity yet (users.status, promo
// rows, generic open/closed). Tones follow the design statusChip() map.
const ADMIN_EXTRA_TONES: Record<string, ChipTone> = {
  blocked: 'danger',
  deleted: 'neutral',
  merged: 'violet',
  grace: 'warn',
  due: 'warn',
  overdue: 'danger',
  void: 'neutral',
  inactive: 'neutral',
};

export function statusChipTone(status: string, entity?: StatusEntity): ChipTone {
  if (entity) return statusToneChip[statusToneOrNeutral(entity, status)];
  for (const e of ENTITY_ORDER) {
    const tone = (STATUS_TONES[e] as Record<string, StatusTone | undefined>)[status];
    if (tone) return statusToneChip[tone];
  }
  return ADMIN_EXTRA_TONES[status] ?? 'neutral';
}

// ---- KPI hue cards (design hue(i) / hueCard / hueTile) ----
export function kpiHue(i: number): { body: string; tile: string; sub: string; line: string; onTile: string } {
  const n = (((i % 4) + 4) % 4) + 1;
  return {
    body: `var(--hue-${n}-body)`,
    tile: `var(--hue-${n}-tile)`,
    sub: `var(--hue-${n}-sub)`,
    line: `var(--hue-${n}-line)`,
    onTile: 'var(--on-tile)',
  };
}

// ---- chart series ----
export const series = {
  s1: 'var(--series-1)',
  s2: 'var(--series-2)',
  s3: 'var(--series-3)',
  pale: 'var(--series-pale)',
  grid: 'var(--chart-grid)',
} as const;

// ---- motion scale (design MO, `A:6966`) — same values as motion.css ----
export const motion = {
  micro: 90,
  fast: 140,
  base: 200,
  slow: 280,
  count: 400,
  enter: 'cubic-bezier(.16,1,.3,1)',
  exit: 'cubic-bezier(.4,0,1,1)',
  move: 'cubic-bezier(.4,0,.2,1)',
  reduced: 80,
} as const;

// ---- layout + breakpoints (VIS §2.4, §4) ----
export const layout = {
  shellRadius: 30,
  railWidth: 268,
  railRadius: 24,
  contentMax: 1160,
  drawerWidth: 560,
} as const;

/** Admin design width checks: isMobile < 768; also < 700, < 900, < 1000, < 1100, ≥ 1180. */
export const breakpoint = {
  mobile: 768,
  compact: 700,
  narrow: 900,
  medium: 1000,
  wide: 1100,
  xwide: 1180,
} as const;

export const radius = {
  pill: '999px',
  card: '20px',
  kpi: '18px',
  control: '12px',
  shell: '30px',
  rail: '24px',
} as const;

/**
 * Data-table page size. The admin design pages tables at 9 rows (VIS §2.4). The
 * contract's PublicSettings (GET /registries/settings) has no table page-size key,
 * so the design value lives here as a named constant, not as a screen literal.
 */
export const TABLE_PAGE_SIZE = 9;
